/**
 * From a viewer failure to a sentence somebody can act on (`KLQ-20260820-001` phase 4).
 *
 * Same rule as every other `problems.ts` here: branch on the problem's `code`, never on the
 * status (`backend/src/kalqa/core/errors.py`, first paragraph).
 *
 * The map itself is `features/drawings`' — this screen family has one error vocabulary, and a
 * second copy of it would be a second place for `not_found` to stop meaning *"gone, and we do
 * not say whether you were allowed to see it"*. What is added here is one code that means
 * something specific on a sheet and nothing in particular anywhere else:
 *
 * - **`conflict` while placing a pin.** The service raises it for exactly two reasons a person
 *   can act on. One is *"this drawing has no published version to pin to"*, which the viewer
 *   makes unreachable — there is no sheet to press without one. The other is the one that
 *   survives: a pin is written against the slot's **current** version, read server-side at the
 *   moment of the write, so a tab left open across somebody else's publish asks to pin a sheet
 *   that is no longer current. The copy says that, and says what to do about it, because
 *   *"something conflicted"* would leave a person retrying the same press.
 */

import { ApiError } from '@/api/client';
import { apiErrorKey } from '@/features/drawings';
import type { MessageKeys } from '@/lib/i18n';

type DrawingsKey = MessageKeys['drawings'];

/** The message key for a failed pin placement. */
export function pinErrorKey(error: unknown): DrawingsKey {
  if (error instanceof ApiError && error.code === 'conflict') return 'pin.error.superseded';
  return apiErrorKey(error);
}
